// src/components/settings/GeneralSettings.js
import React, { useState, useRef, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';

const GeneralSettings = () => {
    const [logoFile, setLogoFile] = useState(null);
    const [faviconFile, setFaviconFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [faviconPreview, setFaviconPreview] = useState(null);
    const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
    const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({ logo: 0, favicon: 0 });
    const [loading, setLoading] = useState({ logo: false, favicon: false });
    const [message, setMessage] = useState({ type: '', text: '' });

    const logoInputRef = useRef(null);
    const faviconInputRef = useRef(null);

    useEffect(() => {
        fetchCurrentBranding();
    }, []);

    const buildAssetUrl = (path) => {
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        return `${process.env.REACT_APP_API_URL}${path}`;
    };

    const fetchCurrentBranding = async () => {
        try {
            const response = await settingsService.getSystemSettings();
            const data = response.data || response;
            const settings = data.settings || [];

            const logoSetting = settings.find((s) => s.setting_key === 'logo');
            const faviconSetting = settings.find((s) => s.setting_key === 'favicon');

            setCurrentLogoUrl(buildAssetUrl(logoSetting?.setting_value || ''));
            setCurrentFaviconUrl(buildAssetUrl(faviconSetting?.setting_value || ''));
        } catch (error) {
            // Keep placeholders if settings fetch fails.
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
            if (!validTypes.includes(file.type)) {
                showMessage('error', 'Invalid file type. Please upload JPG, PNG, or SVG');
                return;
            }

            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                showMessage('error', 'File size must be less than 2MB');
                return;
            }

            setLogoFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleFaviconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png'];
            if (!validTypes.includes(file.type)) {
                showMessage('error', 'Invalid file type. Please upload ICO or PNG');
                return;
            }

            // Validate file size (max 1MB)
            if (file.size > 1 * 1024 * 1024) {
                showMessage('error', 'File size must be less than 1MB');
                return;
            }

            setFaviconFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setFaviconPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoUpload = async () => {
        if (!logoFile) {
            showMessage('error', 'Please select a logo file');
            return;
        }

        setLoading(prev => ({ ...prev, logo: true }));
        try {
            const response = await settingsService.uploadLogo(
                logoFile,
                (progress) => setUploadProgress(prev => ({ ...prev, logo: progress }))
            );

            showMessage('success', 'Logo uploaded successfully');
            const uploadedLogoPath = response?.data?.logo_url || response?.logo_url;
            if (uploadedLogoPath) {
                setCurrentLogoUrl(buildAssetUrl(uploadedLogoPath));
            } else {
                await fetchCurrentBranding();
            }
            setLogoFile(null);
            setLogoPreview(null);
            setUploadProgress(prev => ({ ...prev, logo: 0 }));
        } catch (error) {
            showMessage('error', error.message || 'Failed to upload logo');
        } finally {
            setLoading(prev => ({ ...prev, logo: false }));
        }
    };

    const handleFaviconUpload = async () => {
        if (!faviconFile) {
            showMessage('error', 'Please select a favicon file');
            return;
        }

        setLoading(prev => ({ ...prev, favicon: true }));
        try {
            const response = await settingsService.uploadFavicon(
                faviconFile,
                (progress) => setUploadProgress(prev => ({ ...prev, favicon: progress }))
            );

            showMessage('success', 'Favicon uploaded successfully');
            const uploadedFaviconPath = response?.data?.favicon_url || response?.favicon_url;
            if (uploadedFaviconPath) {
                setCurrentFaviconUrl(buildAssetUrl(uploadedFaviconPath));
            } else {
                await fetchCurrentBranding();
            }
            setFaviconFile(null);
            setFaviconPreview(null);
            setUploadProgress(prev => ({ ...prev, favicon: 0 }));
        } catch (error) {
            showMessage('error', error.message || 'Failed to upload favicon');
        } finally {
            setLoading(prev => ({ ...prev, favicon: false }));
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    };

    return (
        <div className="general-settings">
            <h2 className="section-title">
                <i className="fas fa-image"></i>
                Branding & Appearance
            </h2>

            {message.text && (
                <div className={`message ${message.type}`}>
                    <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
                    {message.text}
                </div>
            )}

            <div className="settings-grid">
                {/* Logo Upload Section */}
                <div className="setting-card">
                    <div className="card-header">
                        <h3>
                            <i className="fas fa-image"></i>
                            Application Logo
                        </h3>
                        <p>Upload your organization's logo. Recommended size: 200x60px</p>
                    </div>

                    <div className="upload-section">
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/svg+xml"
                            onChange={handleLogoChange}
                            style={{ display: 'none' }}
                        />

                        <div className="preview-container">
                            {(logoPreview || currentLogoUrl) ? (
                                <img src={logoPreview || currentLogoUrl} alt="Logo preview" className="preview-image" />
                            ) : (
                                <div className="preview-placeholder">
                                    <i className="fas fa-image"></i>
                                    <p>No logo uploaded</p>
                                </div>
                            )}
                        </div>

                        <div className="upload-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => logoInputRef.current?.click()}
                                disabled={loading.logo}
                            >
                                <i className="fas fa-folder-open"></i>
                                Choose File
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleLogoUpload}
                                disabled={!logoFile || loading.logo}
                            >
                                {loading.logo ? (
                                    <>
                                        <div className="spinner"></div>
                                        Uploading... {uploadProgress.logo}%
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-upload"></i>
                                        Upload Logo
                                    </>
                                )}
                            </button>
                        </div>

                        {loading.logo && uploadProgress.logo > 0 && (
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress.logo}%` }}
                                ></div>
                            </div>
                        )}

                        <div className="file-info">
                            <p><strong>Supported formats:</strong> JPG, PNG, SVG</p>
                            <p><strong>Maximum size:</strong> 2MB</p>
                        </div>
                    </div>
                </div>

                {/* Favicon Upload Section */}
                <div className="setting-card">
                    <div className="card-header">
                        <h3>
                            <i className="fas fa-bookmark"></i>
                            Favicon
                        </h3>
                        <p>Upload your favicon. Recommended size: 32x32px or 16x16px</p>
                    </div>

                    <div className="upload-section">
                        <input
                            ref={faviconInputRef}
                            type="file"
                            accept="image/x-icon,image/vnd.microsoft.icon,image/png"
                            onChange={handleFaviconChange}
                            style={{ display: 'none' }}
                        />

                        <div className="preview-container favicon-preview">
                            {(faviconPreview || currentFaviconUrl) ? (
                                <img src={faviconPreview || currentFaviconUrl} alt="Favicon preview" className="preview-favicon" />
                            ) : (
                                <div className="preview-placeholder">
                                    <i className="fas fa-bookmark"></i>
                                    <p>No favicon uploaded</p>
                                </div>
                            )}
                        </div>

                        <div className="upload-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => faviconInputRef.current?.click()}
                                disabled={loading.favicon}
                            >
                                <i className="fas fa-folder-open"></i>
                                Choose File
                            </button>

                            <button
                                className="btn btn-primary"
                                onClick={handleFaviconUpload}
                                disabled={!faviconFile || loading.favicon}
                            >
                                {loading.favicon ? (
                                    <>
                                        <div className="spinner"></div>
                                        Uploading... {uploadProgress.favicon}%
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-upload"></i>
                                        Upload Favicon
                                    </>
                                )}
                            </button>
                        </div>

                        {loading.favicon && uploadProgress.favicon > 0 && (
                            <div className="progress-bar">
                                <div
                                    className="progress-fill"
                                    style={{ width: `${uploadProgress.favicon}%` }}
                                ></div>
                            </div>
                        )}

                        <div className="file-info">
                            <p><strong>Supported formats:</strong> ICO, PNG</p>
                            <p><strong>Maximum size:</strong> 1MB</p>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .general-settings {
                    animation: fadeIn 0.3s ease-in;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .section-title {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 2rem;
                    color: #2d3748;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .section-title i {
                    color: #667eea;
                }

                .message {
                    padding: 1rem 1.25rem;
                    border-radius: 10px;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    font-weight: 500;
                    animation: slideDown 0.3s ease-out;
                }

                .message.success {
                    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
                    color: #155724;
                    border-left: 4px solid #28a745;
                }

                .message.error {
                    background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
                    color: #721c24;
                    border-left: 4px solid #dc3545;
                }

                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .settings-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
                    gap: 2rem;
                }

                .setting-card {
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 2rem;
                    border: 2px solid #e9ecef;
                    transition: all 0.3s ease;
                }

                .setting-card:hover {
                    border-color: #667eea;
                    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
                }

                .card-header h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    margin: 0 0 0.5rem 0;
                    color: #2d3748;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .card-header p {
                    margin: 0;
                    color: #6c757d;
                    font-size: 0.9rem;
                }

                .upload-section {
                    margin-top: 1.5rem;
                }

                .preview-container {
                    background: white;
                    border: 2px dashed #dee2e6;
                    border-radius: 10px;
                    padding: 2rem;
                    margin-bottom: 1.5rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-height: 150px;
                }

                .favicon-preview {
                    min-height: 100px;
                }

                .preview-image {
                    max-width: 100%;
                    max-height: 150px;
                    object-fit: contain;
                }

                .preview-favicon {
                    width: 64px;
                    height: 64px;
                    object-fit: contain;
                }

                .preview-placeholder {
                    text-align: center;
                    color: #adb5bd;
                }

                .preview-placeholder i {
                    font-size: 3rem;
                    margin-bottom: 0.5rem;
                }

                .preview-placeholder p {
                    margin: 0;
                    font-size: 0.9rem;
                }

                .upload-actions {
                    display: flex;
                    gap: 1rem;
                    margin-bottom: 1rem;
                }

                .progress-bar {
                    width: 100%;
                    height: 8px;
                    background: #e9ecef;
                    border-radius: 4px;
                    overflow: hidden;
                    margin-bottom: 1rem;
                }

                .progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
                    transition: width 0.3s ease;
                }

                .file-info {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: white;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    color: #6c757d;
                }

                .file-info p {
                    margin: 0.25rem 0;
                }

                .spinner {
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 0.5rem;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .settings-grid {
                        grid-template-columns: 1fr;
                    }

                    .upload-actions {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default GeneralSettings;
